import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Banner,
  Button,
  Cell,
  IconButton,
  Input,
  List,
  Placeholder,
  Section,
  Spinner,
} from "@telegram-apps/telegram-ui";

import { api } from "../api";
import { useAppData } from "../AppData";
import { useBackButton } from "../hooks/useBackButton";
import type { Contractor, ContractRequisite } from "../types";

const SHEETS_RE = /^https?:\/\/docs\.google\.com\/spreadsheets\//;

export function DataSettingsPage({ contractsOnly = false }: { contractsOnly?: boolean }) {
  const navigate = useNavigate();
  const { me } = useAppData();

  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [contracts, setContracts] = useState<ContractRequisite[]>([]);
  const [newContractor, setNewContractor] = useState("");
  const [newContract, setNewContract] = useState({ name: "", requisites: "" });
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useBackButton(true, () => navigate("/settings"));

  useEffect(() => {
    api
      .getDataSettings()
      .then((d) => {
        setContractors(d.contractors);
        setContracts(d.contracts);
      })
      .catch((e: unknown) =>
        setLoadError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setLoading(false));
  }, []);

  const addContractor = () => {
    const name = newContractor.trim();
    if (!name || contractors.some((c) => c.name === name)) {
      setNewContractor("");
      return;
    }
    setContractors((prev) => [...prev, { name, price_table_url: "" }]);
    setNewContractor("");
  };

  const removeContractor = (name: string) => {
    setContractors((prev) => prev.filter((c) => c.name !== name));
  };

  const setUrl = (idx: number, url: string) => {
    setContractors((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, price_table_url: url } : c)),
    );
  };

  const addContract = () => {
    const { name, requisites } = newContract;
    if (!name.trim() || contracts.some((c) => c.name === name.trim())) {
      return;
    }
    setContracts((prev) => [...prev, { name: name.trim(), requisites: requisites.trim() }]);
    setNewContract({ name: "", requisites: "" });
  };

  const removeContract = (name: string) => {
    setContracts((prev) => prev.filter((c) => c.name !== name));
  };

  const setRequisite = (idx: number, reqs: string) => {
    setContracts((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, requisites: reqs } : c)),
    );
  };

  const urlValid = (url: string) => !url.trim() || SHEETS_RE.test(url.trim());
  const allValid = contractors.every((c) => urlValid(c.price_table_url));

  const save = async () => {
    if (saving || !allValid) return;
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const d = await api.updateDataSettings({
        contractors: contractors.map((c) => ({
          name: c.name,
          price_table_url: c.price_table_url.trim(),
        })),
        contracts: contracts.map((c) => ({
          name: c.name,
          requisites: c.requisites.trim(),
        })),
      });
      setContractors(d.contractors);
      setContracts(d.contracts);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  if (me && !me.is_admin) {
    return <Navigate to="/settings" replace />;
  }

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: "center" }}>
        <Spinner size="m" />
      </div>
    );
  }

  if (loadError) {
    return <Placeholder header="Ошибка" description={loadError} />;
  }

  return (
    <div className="page">
      <List>
        {!contractsOnly && (
          <>
            <Section
              header="Добавить контрагента"
              footer="У каждого контрагента своя прайс-таблица Google Sheets с колонками «Ссылка» и «Цена» — по ней считается сумма в заявке."
            >
              <div style={{ display: "flex", gap: 8, padding: "8px 16px", alignItems: "center" }}>
                <div style={{ flex: 1 }}>
                  <Input
                    value={newContractor}
                    onChange={(e) => setNewContractor(e.target.value)}
                    placeholder="Название контрагента"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addContractor();
                    }}
                  />
                </div>
                <Button size="m" onClick={addContractor} disabled={!newContractor.trim()}>
                  Добавить
                </Button>
              </div>
            </Section>

            {contractors.length === 0 ? (
              <Section>
                <Cell subtitle="Добавьте хотя бы одного контрагента выше.">
                  Контрагентов пока нет
                </Cell>
              </Section>
            ) : (
              contractors.map((c, idx) => (
                <Section
                  key={c.name}
                  header={c.name}
                >
                  <Input
                    header="Прайс-таблица"
                    value={c.price_table_url}
                    onChange={(e) => setUrl(idx, e.target.value)}
                    placeholder="https://docs.google.com/spreadsheets/d/..."
                    status={c.price_table_url && !urlValid(c.price_table_url) ? "error" : "default"}
                  />
                  <Cell
                    onClick={() => removeContractor(c.name)}
                    style={{ cursor: "pointer" }}
                    after={
                      <IconButton mode="plain" size="s" aria-label="Удалить">
                        ✕
                      </IconButton>
                    }
                  >
                    Удалить контрагента
                  </Cell>
                </Section>
              ))
            )}
          </>
        )}

        {contractsOnly && (
          <>
            <Section
              header="Добавить договор"
              footer="Введите название контрагента и реквизиты договора."
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "8px 16px" }}>
                <Input
                  value={newContract.name}
                  onChange={(e) => setNewContract({ ...newContract, name: e.target.value })}
                  placeholder="Название контрагента"
                />
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <Input
                    style={{ flex: 1 }}
                    value={newContract.requisites}
                    onChange={(e) => setNewContract({ ...newContract, requisites: e.target.value })}
                    placeholder="Реквизиты договора (№ и дата)"
                  />
                  <Button size="m" onClick={addContract} disabled={!newContract.name.trim() || !newContract.requisites.trim()}>
                    Добавить
                  </Button>
                </div>
              </div>
            </Section>

            {contracts.length === 0 ? (
              <Section>
                <Cell subtitle="Добавьте хотя бы один договор выше.">
                  Договоров пока нет
                </Cell>
              </Section>
            ) : (
              contracts.map((c, idx) => (
                <Section
                  key={c.name}
                  header={c.name}
                >
                  <Input
                    header="Реквизиты договора"
                    value={c.requisites}
                    onChange={(e) => setRequisite(idx, e.target.value)}
                    placeholder="дог. №..."
                  />
                  <Cell
                    onClick={() => removeContract(c.name)}
                    style={{ cursor: "pointer" }}
                    after={
                      <IconButton mode="plain" size="s" aria-label="Удалить">
                        ✕
                      </IconButton>
                    }
                  >
                    Удалить договор
                  </Cell>
                </Section>
              ))
            )}
          </>
        )}

        {saveError && (
          <div style={{ padding: "8px 16px" }}>
            <Banner type="section" header="Не удалось сохранить" description={saveError} />
          </div>
        )}

        <div style={{ padding: "8px 16px" }}>
          <Button
            size="l"
            stretched
            loading={saving}
            disabled={saving || !allValid}
            onClick={save}
          >
            {saved ? "Сохранено" : "Сохранить"}
          </Button>
        </div>
      </List>
    </div>
  );
}
